import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/seo/SEO";
import { Helmet } from "react-helmet-async";
import { SkipLink } from "@/components/common/SkipLink";
import { CookieConsent } from "@/components/common/CookieConsent";
import { Toaster } from "@/components/ui/toaster";
import { StickyCTA } from "@/components/common/StickyCTA";
import { WhatsAppButton } from "@/components/common/WhatsAppButton";
import { getServiceBySlug, getServiceTranslation, resolveLocale } from "@/data/serviceDetails";

const ServiceDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const locale = resolveLocale(i18n.language || "en");
  const service = useMemo(() => (slug ? getServiceBySlug(slug) : undefined), [slug]);

  if (!service) {
    return (
      <div className="bg-neutral-50 min-h-screen text-neutral-900 flex flex-col">
        <SkipLink />
        <Header />
        <main className="flex-grow flex items-center justify-center px-6 py-24">
          <div className="max-w-lg text-center space-y-6">
            <h1 className="text-3xl font-bold">{t("common.not_found", "Content not found")}</h1>
            <p className="text-neutral-600">
              {t("common.not_found_message", "The project you are looking for could not be found. Please choose another service.")}
            </p>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-full border border-brand-turquoise bg-brand-turquoise/10 px-6 py-2 text-sm font-semibold text-brand-turquoise transition hover:bg-brand-turquoise hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("common.go_back", "Go back")}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const serviceContent = getServiceTranslation(service, locale);

  const baseUrl = 'https://www.insorganizasyon.org';
  const serviceUrl = `${baseUrl}/projeler/${service.slug}`;
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: serviceContent.heroTitle, item: serviceUrl }
    ]
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: serviceContent.heroTitle,
    description: serviceContent.heroSubtitle,
    provider: { '@type': 'Organization', name: 'INS Organization' },
    url: serviceUrl
  };

  return (
    <div className="bg-neutral-50 min-h-screen text-neutral-900 flex flex-col">
      <SkipLink />
      <SEO title={serviceContent.heroTitle} description={serviceContent.heroSubtitle} url={`/projeler/${service.slug}`} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>
      <Header />
      <main id="main-content" className="flex-grow">
        <section className="relative overflow-hidden bg-gradient-to-br from-brand-turquoise/10 via-white to-white py-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="container mx-auto px-4 max-w-5xl relative"
          >
            <motion.div
              className="absolute -top-24 -right-10 h-48 w-48 rounded-full bg-brand-turquoise/20 blur-3xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 1.2 }}
            />
            <button
              onClick={() => navigate(-1)}
              className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-brand-turquoise hover:text-teal-700 transition"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("common.back", "Back")}
            </button>
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900">
              {serviceContent.heroTitle}
            </h1>
            <p className="mt-6 max-w-3xl text-lg text-neutral-700">
              {serviceContent.heroSubtitle}
            </p>
            <div className="mt-10 grid gap-4 md:grid-cols-3">
              {serviceContent.highlights.map((highlight) => (
                <motion.div
                  key={highlight}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.7 }}
                  transition={{ duration: 0.4 }}
                  className="rounded-2xl border border-brand-turquoise/30 bg-white/90 p-4 text-sm font-semibold text-brand-turquoise shadow-sm"
                >
                  {highlight}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 max-w-5xl space-y-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.5 }}
              className="rounded-3xl border border-neutral-200 bg-white/95 p-8 shadow-sm backdrop-blur"
            >
              <h2 className="text-2xl font-semibold text-neutral-900">
                {t("projects.detail_overview", "Our approach")}
              </h2>
              <div className="mt-6 space-y-4 text-neutral-700 leading-relaxed">
                {serviceContent.overview.map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.5 }}
              className="rounded-3xl border border-brand-turquoise/20 bg-brand-turquoise/5 p-8 shadow-sm"
            >
              <h2 className="text-2xl font-semibold text-neutral-900">
                {t("transparency.proofTitle", "Proof of Delivery at Every Step")}
              </h2>
              <ul className="mt-6 grid gap-3 text-sm font-medium text-neutral-700 sm:grid-cols-2">
                {(t("transparency.evidenceList", { returnObjects: true }) as string[]).map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <span className="h-2.5 w-2.5 rounded-full bg-brand-turquoise" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <div className="flex justify-center md:justify-start">
              <a
                href="/#regions"
                className="inline-flex items-center gap-2 rounded-full border border-brand-turquoise/30 bg-white px-6 py-3 text-sm font-semibold text-brand-turquoise shadow-sm transition hover:border-brand-turquoise hover:bg-brand-turquoise/10"
              >
                {t("projects.back_to_regions", "Back to regions")}
              </a>
            </div>

          </div>
        </section>
      </main>
      <Footer />
      <CookieConsent />
      <Toaster />
      <StickyCTA />
      <WhatsAppButton />
    </div>
  );
};

export default ServiceDetailPage;
